/**
 * Patient Edit Modal Component
 * Feature: 004-restore-missing-features
 * Task: T015, T017, T019, T020
 *
 * Three-step wizard for editing patient data and family members
 * Step 1: Patient information (with option to save and close)
 * Step 2: Family members management (changes saved in real-time)
 * Step 3: Scheduling configuration
 */

import { useState, useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { Loader2, AlertCircle } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { supabase } from '@/lib/supabase'
import { useUpdatePatient } from '../hooks/use-patients'
import { useFamilyMembers } from '../hooks/use-family-members'
import { StepIndicator, type Step } from './StepIndicator'
import { FamilyMemberCard } from './FamilyMemberCard'
import { FamilyMemberForm } from './FamilyMemberForm'
import { PhoneInputWithDDI } from '@/features/shared/components/PhoneInputWithDDI'
import { extractLocalNumber } from '@/features/shared/utils/phoneUtils'
import type { FamilyMember, RelationshipType } from '@/features/shared/types/patient.types'
import { ScheduleFormStep } from '@/features/scheduling/components/ScheduleFormStep'
import {
  updatePatientFormSchema,
  type UpdatePatientFormData,
} from '../schemas/patient-form.schema'

// ============================================================================
// Types
// ============================================================================

// Using a flexible interface that accepts the patient detail shape from the hook
// The family_members are fetched separately via useFamilyMembers hook anyway
interface PatientWithFamily {
  id: string
  full_name: string
  birth_date: string | null
  address: string | null
  emergency_contact: string | null
  emergency_contact_country_code?: string | null
  condition_description: string | null
  medical_history: string | null
  current_medications: string[] | null
  status: 'active' | 'inactive' | 'discharged' | 'deleted'
  // Family members are managed separately via useFamilyMembers hook
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  family_members?: any[]
}

interface PatientEditModalProps {
  patient: PatientWithFamily | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
  initialStep?: WizardStep
}

type WizardStep = 'patient' | 'family' | 'scheduling'

const WIZARD_STEPS: Step[] = [
  { id: 'patient', label: 'Dados do Paciente' },
  { id: 'family', label: 'Familiares' },
  { id: 'scheduling', label: 'Agendamento' },
]

// ============================================================================
// Component
// ============================================================================

export function PatientEditModal({
  patient,
  open,
  onOpenChange,
  onSuccess,
  initialStep = 'patient',
}: PatientEditModalProps) {
  // State
  const [step, setStep] = useState<WizardStep>('patient')
  const [isAddingMember, setIsAddingMember] = useState(false)
  const [editingMember, setEditingMember] = useState<FamilyMember | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Use ref to track initialization - refs don't trigger re-renders
  const lastPatientIdRef = useRef<string | null>(null)
  const hasInitializedRef = useRef(false)

  // Hooks
  const updatePatient = useUpdatePatient()
  const familyMembersHook = useFamilyMembers(patient?.id)

  // Query to fetch family members - auto-updates when mutations invalidate
  const { data: familyMembersData } = useQuery({
    queryKey: ['patient-family-members', patient?.id],
    queryFn: async () => {
      if (!patient?.id) return []
      const { data, error } = await supabase
        .from('family_members')
        .select(`
          *,
          profiles (
            id,
            full_name,
            email,
            phone
          )
        `)
        .eq('patient_id', patient.id)
        .order('is_primary_contact', { ascending: false })

      if (error) throw error
      return data || []
    },
    enabled: !!patient?.id && open,
  })

  // Form
  const form = useForm<UpdatePatientFormData>({
    resolver: zodResolver(updatePatientFormSchema),
    defaultValues: {
      full_name: '',
      birth_date: '',
      address: '',
      emergency_contact: '',
      emergency_contact_country_code: '55',
      condition_description: '',
      medical_history: '',
      current_medications: '',
      status: 'active',
    },
  })

  // Initialize form ONLY when opening modal with a different patient
  // Using refs to avoid any state-based re-trigger issues
  useEffect(() => {
    if (open && patient) {
      const isNewPatient = patient.id !== lastPatientIdRef.current
      const needsInit = !hasInitializedRef.current || isNewPatient

      if (needsInit) {
        lastPatientIdRef.current = patient.id
        hasInitializedRef.current = true
        const emergencyCountryCode = patient.emergency_contact_country_code ?? '55'
        form.reset({
          full_name: patient.full_name,
          birth_date: patient.birth_date?.split('T')[0] ?? '',
          address: patient.address ?? '',
          emergency_contact: patient.emergency_contact
            ? extractLocalNumber(patient.emergency_contact, emergencyCountryCode)
            : '',
          emergency_contact_country_code: emergencyCountryCode,
          condition_description: patient.condition_description ?? '',
          medical_history: patient.medical_history ?? '',
          current_medications: patient.current_medications?.join('\n') ?? '',
          status: patient.status,
        })
        setStep(initialStep)
        setError(null)
      }
    }
  }, [open, patient?.id, initialStep, form]) // Only depend on patient.id, not the whole patient object

  // Reset refs when modal closes so next open will reinitialize
  useEffect(() => {
    if (!open) {
      // Small delay to ensure this runs after close animation
      const timeout = setTimeout(() => {
        hasInitializedRef.current = false
        setStep(initialStep)
        setIsAddingMember(false)
        setEditingMember(null)
        setError(null)
      }, 100)
      return () => clearTimeout(timeout)
    }
  }, [open, initialStep])

  // ============================================================================
  // Handlers
  // ============================================================================

  const handleNextStep = async () => {
    if (step === 'patient') {
      const isValid = await form.trigger()
      if (isValid) setStep('family')
    } else if (step === 'family') {
      setStep('scheduling')
    }
  }

  const handlePrevStep = () => {
    if (step === 'family') setStep('patient')
    else if (step === 'scheduling') setStep('family')
  }

  const handleSavePatient = async () => {
    if (!patient) return

    const isValid = await form.trigger()
    if (!isValid) return

    try {
      setError(null)
      const data = form.getValues()

      await updatePatient.mutateAsync({
        id: patient.id,
        full_name: data.full_name,
        birth_date: data.birth_date ? new Date(data.birth_date).toISOString() : undefined,
        address: data.address || undefined,
        emergency_contact: data.emergency_contact || undefined,
        emergency_contact_country_code: data.emergency_contact_country_code || '55',
        condition_description: data.condition_description || undefined,
        medical_history: data.medical_history || undefined,
        current_medications: data.current_medications
          ? data.current_medications.split('\n').filter((m) => m.trim())
          : undefined,
        status: data.status,
      })

      onOpenChange(false)
      onSuccess?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar paciente')
    }
  }

  const handleAddFamilyMember = async (data: {
    name: string
    email?: string
    phone?: string
    country_code: string
    relationship: RelationshipType
    is_primary_contact: boolean
    accepts_notifications: boolean
  }) => {
    if (!patient) return

    try {
      setError(null)
      await familyMembersHook.createMember({
        patient_id: patient.id,
        name: data.name,
        email: data.email,
        phone: data.phone,
        country_code: data.country_code,
        relationship: data.relationship,
        is_primary_contact: data.is_primary_contact,
        accepts_notifications: data.accepts_notifications,
      })
      setIsAddingMember(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao adicionar familiar')
    }
  }

  const handleUpdateFamilyMember = async (data: {
    name: string
    email?: string
    phone?: string
    country_code: string
    relationship: RelationshipType
    is_primary_contact: boolean
    accepts_notifications: boolean
  }) => {
    if (!editingMember) return

    try {
      setError(null)
      await familyMembersHook.updateMember({
        id: editingMember.id,
        name: data.name,
        email: data.email,
        phone: data.phone,
        country_code: data.country_code,
        relationship: data.relationship,
        is_primary_contact: data.is_primary_contact,
        accepts_notifications: data.accepts_notifications,
      })
      setEditingMember(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao atualizar familiar')
    }
  }

  const handleDeleteFamilyMember = async (memberId: string) => {
    try {
      setError(null)
      await familyMembersHook.deleteMember(memberId)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao remover familiar')
    }
  }

  const handleSetPrimary = async (memberId: string) => {
    if (!patient) return

    try {
      setError(null)
      await familyMembersHook.setPrimaryContact(memberId, patient.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao definir contato primário')
    }
  }

  const handleClose = () => {
    onOpenChange(false)
    onSuccess?.()
  }

  // Use data from query for real-time updates, fallback to patient prop
  const familyMembers = familyMembersData || patient?.family_members || []
  const isLoading = updatePatient.isPending || familyMembersHook.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Editar Paciente</DialogTitle>
          <DialogDescription>
            {step === 'patient'
              ? 'Atualize os dados do paciente'
              : step === 'family'
                ? 'Gerencie os familiares e contatos'
                : 'Configure o agendamento'}
          </DialogDescription>
        </DialogHeader>

        {/* Step Indicator */}
        <div className="py-2">
          <StepIndicator
            steps={WIZARD_STEPS}
            currentStep={step}
            completedSteps={
              step === 'scheduling'
                ? ['patient', 'family']
                : step === 'family'
                  ? ['patient']
                  : []
            }
          />
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Step 1: Patient Data */}
        {step === 'patient' && (
          <Form {...form}>
            <form className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="full_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nome Completo *</FormLabel>
                      <FormControl>
                        <Input placeholder="Nome do paciente" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="birth_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Data de Nascimento</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Endereço</FormLabel>
                    <FormControl>
                      <Input placeholder="Endereço completo" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="emergency_contact"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <FormLabel>Contato de Emergência</FormLabel>
                      <FormControl>
                        <PhoneInputWithDDI
                          value={field.value ?? ''}
                          countryCode={form.watch('emergency_contact_country_code')}
                          onPhoneChange={field.onChange}
                          onCountryCodeChange={(code) => form.setValue('emergency_contact_country_code', code)}
                          error={fieldState.error?.message}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="status"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Status</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="active">Ativo</SelectItem>
                          <SelectItem value="inactive">Inativo</SelectItem>
                          <SelectItem value="discharged">Alta</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="condition_description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Descrição da Condição</FormLabel>
                    <FormControl>
                      <textarea
                        placeholder="Ex: TEA - Nível 1"
                        className="w-full min-h-[80px] px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-teal-500"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="current_medications"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Medicamentos Atuais</FormLabel>
                    <FormControl>
                      <textarea
                        placeholder="Um medicamento por linha..."
                        className="w-full min-h-[60px] px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-teal-500"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="medical_history"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Histórico Médico</FormLabel>
                    <FormControl>
                      <textarea
                        placeholder="Descreva o histórico médico relevante..."
                        className="w-full min-h-[80px] px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-teal-500"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </form>
          </Form>
        )}

        {/* Step 2: Family Members */}
        {step === 'family' && (
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-500">
                {familyMembers.length > 0
                  ? `${familyMembers.length} familiar(es) cadastrado(s)`
                  : 'Nenhum familiar cadastrado'}
              </p>
            </div>

            {/* Add/Edit Member Form */}
            {(isAddingMember || editingMember) && (
              <FamilyMemberForm
                initialData={
                  editingMember
                    ? {
                        name: editingMember.profiles?.full_name || '',
                        email: editingMember.profiles?.email || '',
                        phone: editingMember.whatsapp_number || '',
                        country_code: editingMember.country_code ?? '55',
                        relationship: editingMember.relationship,
                        is_primary_contact: editingMember.is_primary_contact,
                        accepts_notifications: editingMember.accepts_notifications ?? false,
                      }
                    : undefined
                }
                onSubmit={editingMember ? handleUpdateFamilyMember : handleAddFamilyMember}
                onCancel={() => {
                  setIsAddingMember(false)
                  setEditingMember(null)
                }}
                isSubmitting={familyMembersHook.isPending}
                isEditing={!!editingMember}
                existingPrimaryCount={familyMembers.filter((m: FamilyMember) => m.is_primary_contact).length}
              />
            )}

            {/* Family Members List */}
            {!isAddingMember && !editingMember && (
              <div className="space-y-2">
                {familyMembers.map((member) => (
                  <FamilyMemberCard
                    key={member.id}
                    member={member}
                    onEdit={setEditingMember}
                    onDelete={handleDeleteFamilyMember}
                    onSetPrimary={handleSetPrimary}
                    isLoading={familyMembersHook.isPending}
                    compact
                  />
                ))}

                <Button
                  type="button"
                  variant="outline"
                  className="w-full"
                  onClick={() => setIsAddingMember(true)}
                >
                  Adicionar Familiar
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Scheduling */}
        {step === 'scheduling' && patient && (
          <ScheduleFormStep patientId={patient.id} />
        )}

        {/* Footer */}
        <DialogFooter className="gap-2 sm:gap-0">
          {step === 'patient' && (
            <>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={handleSavePatient}
                disabled={isLoading}
              >
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Salvar e Fechar
              </Button>
              <Button type="button" onClick={handleNextStep}>
                Próximo
              </Button>
            </>
          )}
          {step === 'family' && (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={handlePrevStep}
                disabled={isAddingMember || !!editingMember}
              >
                Voltar
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={handleClose}
                disabled={isAddingMember || !!editingMember}
              >
                Concluir
              </Button>
              <Button
                type="button"
                onClick={handleNextStep}
                disabled={isAddingMember || !!editingMember}
              >
                Próximo
              </Button>
            </>
          )}
          {step === 'scheduling' && (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={handlePrevStep}
              >
                Voltar
              </Button>
              <Button
                type="button"
                onClick={handleClose}
              >
                Concluir
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
